import {
  getFeatureRuleValueError,
  RANGE_INCOMPLETE_MESSAGE,
  RANGE_REVERSED_MESSAGE,
} from '@afriex/core/domain/entities/FeatureValidator';

import type {
  AfriexFeature,
  AfriexFeatureLimit,
  AfriexFeatureMetaFlags,
  AfriexFeatureTagMap,
} from '@/types';
import {
  AfriexCountryCode,
  AfriexFeatureField,
  AfriexFeatureFrequency,
  AfriexOS,
  AfriexRegionCode,
  BusinessFeeComparator,
} from '@/types';
import {isolateErrorSync} from '@/utils/dashboard';

import {ValidIsoDateTime, ValidUserID} from './createValidator';

export const OPERATOR_DELIMITER =
  /_EQUALS_|_BETWEEN_|_GREATER_THAN_|_LESS_THAN_/;

const EmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const IPv4Regex =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

const IPv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;

export class FeatureValidator {
  constructor(private request: Partial<AfriexFeature>) {}

  private throwErrorMessage(errorMessage: string): never {
    throw new Error(errorMessage);
  }

  private validateName() {
    if (!this.request?.name) {
      this.throwErrorMessage('No feature name provided');
    }
    return this;
  }

  private validateCode() {
    if (!this.request?.code) {
      this.throwErrorMessage('No feature flag code provided');
    }
    return this;
  }

  private validateCountryTag(value: string) {
    const countries = value.split(',').map((v) => v.trim());
    countries.forEach((country) => {
      if (
        !Object.values(AfriexCountryCode).includes(country as AfriexCountryCode)
      ) {
        this.throwErrorMessage(
          `Invalid country code provided for feature tag: ${country}`,
        );
      }
    });
  }

  private validateRegionTag(value: string) {
    const regions = value.split(',').map((v) => v.trim());
    regions.forEach((region) => {
      if (
        !Object.values(AfriexRegionCode).includes(region as AfriexRegionCode)
      ) {
        this.throwErrorMessage(
          `Invalid region provided for feature tag: ${region}`,
        );
      }
    });
  }

  private validateUserIdTag(value: string) {
    const userIds = value.split(',').map((v) => v.trim());
    userIds.forEach((userId) => {
      const [, validationError] = isolateErrorSync(() =>
        ValidUserID.validate(userId),
      );
      if (validationError) {
        this.throwErrorMessage(
          `Invalid user ID provided for feature tag: ${userId}`,
        );
      }
    });
  }

  private validateBusinessTag(value: string) {
    // Business IDs use same validation as User IDs
    const businessIds = value.split(',').map((v) => v.trim());
    businessIds.forEach((businessId) => {
      const [, validationError] = isolateErrorSync(() =>
        ValidUserID.validate(businessId),
      );
      if (validationError) {
        this.throwErrorMessage(
          `Invalid business ID provided for feature tag: ${businessId}`,
        );
      }
    });
  }

  private validateEmailTag(value: string) {
    const emails = value.split(',').map((v) => v.trim());
    emails.forEach((email) => {
      if (!EmailRegex.test(email)) {
        this.throwErrorMessage(
          `Invalid email provided for feature tag: ${email}`,
        );
      }
    });
  }

  private isValidConstantString(value: string): boolean {
    // Must start with uppercase letter, followed by any combination of
    // uppercase letters, numbers, and underscores
    const VALID_PATTERN = /^[A-Z][A-Z0-9_]*$/;
    return VALID_PATTERN.test(value);
  }

  private validateGroupTag(value: string) {
    const groups = value.split(',').map((v) => v.trim());
    groups.forEach((group) => {
      if (!this.isValidConstantString(group)) {
        this.throwErrorMessage(
          `Invalid group provided for feature tag: ${group}`,
        );
      }
    });
  }

  private validateRoleTag(value: string) {
    const roles = value.split(',').map((v) => v.trim());
    roles.forEach((role) => {
      if (!this.isValidConstantString(role)) {
        this.throwErrorMessage(
          `Invalid role provided for feature tag: ${role}`,
        );
      }
    });
  }

  private validateOSTag(value: string) {
    const osList = value.split(',').map((v) => v.trim());
    osList.forEach((os) => {
      if (!Object.values(AfriexOS).includes(os as AfriexOS)) {
        this.throwErrorMessage(`Invalid OS provided for feature tag: ${os}`);
      }
    });
  }

  private validateIPTag(value: string) {
    const ips = value.split(',').map((v) => v.trim());
    ips.forEach((ip) => {
      if (!IPv4Regex.test(ip) && !IPv6Regex.test(ip)) {
        this.throwErrorMessage(
          `Invalid IP address provided for feature tag: ${ip}`,
        );
      }
    });
  }

  private validateSignupDateTag(value: string) {
    const dates = value.split(',').map((v) => v.trim());
    dates.forEach((date) => {
      const [, validationError] = isolateErrorSync(() =>
        ValidIsoDateTime.validate(date),
      );
      if (validationError) {
        this.throwErrorMessage(
          `Invalid signup date provided for feature tag: ${date}`,
        );
      }
    });
  }

  private validateTagField(field: AfriexFeatureField, value: string) {
    switch (field) {
      // case AfriexFeatureField.Currency:
      //   return this.validateCurrencyTag(value);
      case AfriexFeatureField.Country:
        return this.validateCountryTag(value);
      case AfriexFeatureField.Region:
        return this.validateRegionTag(value);
      case AfriexFeatureField.UserId:
        return this.validateUserIdTag(value);
      case AfriexFeatureField.Business:
        return this.validateBusinessTag(value);
      case AfriexFeatureField.Email:
        return this.validateEmailTag(value);
      case AfriexFeatureField.Group:
        return this.validateGroupTag(value);
      case AfriexFeatureField.Role:
        return this.validateRoleTag(value);
      case AfriexFeatureField.OS:
        return this.validateOSTag(value);
      case AfriexFeatureField.IP:
        return this.validateIPTag(value);
      case AfriexFeatureField.SignupDate:
        return this.validateSignupDateTag(value);
      case AfriexFeatureField.TransactionCount:
      case AfriexFeatureField.TransactionVolume:
        // Checked with the comparator by getFeatureRuleValueError.
        return;
      default:
        // Exhaustive over the currently-enabled AfriexFeatureField members, so
        // `field` is typed `never` here; a value that reaches this branch at
        // runtime (an arbitrary string cast to the enum type upstream) is not,
        // hence the explicit String() rather than a bare interpolation.
        this.throwErrorMessage(`Unknown feature field: ${String(field)}`);
    }
  }

  private validateTags(tags?: AfriexFeatureTagMap) {
    if (!tags) {
      this.throwErrorMessage('Filters are required');
    }

    Object.entries(tags).forEach(([field, valueWithComparator]) => {
      if (!valueWithComparator) {
        this.throwErrorMessage('Filter value is missing for field: ' + field);
      }

      if (
        !Object.values(AfriexFeatureField).includes(field as AfriexFeatureField)
      ) {
        this.throwErrorMessage(`Invalid feature tag field: ${field}`);
      }
      const [comparator, value, ...extraSegments] =
        valueWithComparator.split('::');
      if (
        !Object.values(BusinessFeeComparator).includes(
          comparator as BusinessFeeComparator,
        )
      ) {
        this.throwErrorMessage(
          `Invalid comparator provided for feature filter: ${field}: ${comparator}`,
        );
      }
      if (value === undefined || extraSegments.length > 0) {
        this.throwErrorMessage(
          `Invalid value provided for feature filter: ${field}: ${valueWithComparator}`,
        );
      }
      const valueError = getFeatureRuleValueError(
        field as AfriexFeatureField,
        comparator as BusinessFeeComparator,
        value,
      );
      if (valueError) {
        // The range messages are worded for the Add form, which shows the
        // field; a saved flag can carry several rules, so name it here.
        this.throwErrorMessage(
          valueError === RANGE_INCOMPLETE_MESSAGE ||
            valueError === RANGE_REVERSED_MESSAGE
            ? `${valueError} (feature filter: ${field})`
            : valueError,
        );
      }
      this.validateTagField(field as AfriexFeatureField, value);
    });

    return this;
  }

  private validateLimit(limit?: AfriexFeatureLimit) {
    if (!limit) {
      return this;
    }

    if (!limit.type || !['PERCENTAGE', 'COUNT'].includes(limit.type)) {
      this.throwErrorMessage('Invalid limit type provided');
    }

    if (typeof limit.value !== 'number' || limit.value < 0) {
      this.throwErrorMessage('Invalid limit value provided');
    }

    if (
      limit.frequency &&
      !Object.values(AfriexFeatureFrequency).includes(limit.frequency)
    ) {
      this.throwErrorMessage('Invalid limit frequency provided');
    }

    // Additional validation for percentage type
    if (limit.type === 'PERCENTAGE' && limit.value > 100) {
      this.throwErrorMessage('Percentage limit cannot exceed 100');
    }

    return this;
  }

  private validateMetaFlags(flags?: AfriexFeatureMetaFlags) {
    if (!flags) {
      return this;
    }

    if (
      flags.isDeactivated !== undefined &&
      typeof flags.isDeactivated !== 'boolean'
    ) {
      this.throwErrorMessage('Invalid isDeactivated flag provided');
    }

    if (
      flags.shouldIgnoreLimit !== undefined &&
      typeof flags.shouldIgnoreLimit !== 'boolean'
    ) {
      this.throwErrorMessage('Invalid shouldIgnoreLimit flag provided');
    }

    if (
      flags.shouldIgnoreFlags !== undefined &&
      typeof flags.shouldIgnoreFlags !== 'boolean'
    ) {
      this.throwErrorMessage('Invalid shouldIgnoreFlags flag provided');
    }

    return this;
  }

  private validateMeta() {
    if (!this.request?.meta) {
      return this;
    }

    const {meta} = this.request;

    if (meta.tags) {
      this.validateTags(meta.tags);
    }

    if (meta.flags) {
      this.validateMetaFlags(meta.flags);
    }

    if (meta?.limit?.value) {
      this.validateLimit(meta.limit);
    }

    return this;
  }

  private validate() {
    this.validateCode();
    this.validateName();
    this.validateMeta();
  }

  value(): AfriexFeature {
    this.validate();
    return this.request as AfriexFeature;
  }
}
